'use client';

import { useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  HOUR_OPTIONS,
  MINUTE_OPTIONS,
  TimeUnitInput,
} from '@/components/ui/time-unit-input';

interface GridAddTimeRowProps {
  /** Receives `HH:mm`. The parent is responsible for deduplicating against existing rows. */
  onAdd: (time: string) => void;
}

/**
 * Adds a row for a time nobody declared and no match occupies yet.
 *
 * Without this, a time can only enter the grid by being declared in a block or by already
 * holding a match, so moving the first match to (say) 18:00 would mean opening the fine-grained
 * modal, typing it by hand, and only then having a row to drag the rest onto.
 *
 * Reuses `TimeUnitInput` — the same hour/minute pair `MatchDateTimeEditModal` uses — so the
 * grid and the modal agree on what a valid time looks like (minutes snap to :00/:15/:30/:45).
 */
export function GridAddTimeRow({ onAdd }: GridAddTimeRowProps) {
  const [hour, setHour] = useState('');
  const [minute, setMinute] = useState('');

  const isComplete = hour.length === 2 && minute.length === 2;

  const handleAdd = () => {
    if (!isComplete) return;
    onAdd(`${hour}:${minute}`);
    setHour('');
    setMinute('');
  };

  return (
    <div className="flex flex-wrap items-center gap-2 pt-2">
      <span className="text-xs text-gray-500">Agregar horario</span>
      <TimeUnitInput
        id="grid-add-hour"
        value={hour}
        onChange={setHour}
        options={HOUR_OPTIONS}
        maxValue={23}
        placeholder="Hora"
        ariaLabel="Horas"
      />
      <span className="select-none text-muted-foreground" aria-hidden>
        :
      </span>
      <TimeUnitInput
        id="grid-add-minute"
        value={minute}
        onChange={setMinute}
        options={MINUTE_OPTIONS}
        maxValue={59}
        placeholder="Min"
        ariaLabel="Minutos"
      />
      <Button size="sm" variant="outline" onClick={handleAdd} disabled={!isComplete}>
        <Plus className="mr-1 h-4 w-4" />
        Agregar
      </Button>
    </div>
  );
}
